import { IPActionButton, IPSpinner, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { eventIcon, globalStackTokensGapSmall, taskIcon } from 'globalStyles';
import { getTheme } from '@fluentui/react';
import Task, { TaskTypes } from 'models/tasks/task';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface IFollowUpTemplateListProps {
  templateItems: Task[];
  isLoading: boolean;
  onSelect: (template: Task) => void;
  maxHeight?: number;
}

/**
 * Shared template list rendering for follow-up flows.
 * Used by TaskFollowUpDialog.
 * Memoized to prevent re-renders when parent state changes (e.g. skip panel toggling).
 */
export const FollowUpTemplateList = React.memo((props: IFollowUpTemplateListProps) => {
  const { t } = useTranslation(['task']);
  const theme = getTheme();

  if (props.isLoading) {
    return (
      <IPStack horizontal tokens={globalStackTokensGapSmall} styles={{ root: { padding: '16px 0' } }}>
        <IPSpinner />
        <IPText>{t('task:TopTasks.TemplatesLoading')}</IPText>
      </IPStack>
    );
  }

  if (props.templateItems.length === 0) {
    return (
      <IPText variant="small" styles={{ root: { color: theme.palette.neutralSecondary, padding: '16px 0' } }}>
        {t('task:TopTasks.NoTemplateItemsUser')}
      </IPText>
    );
  }

  return (
    <div role="list" style={{ maxHeight: props.maxHeight ?? 400, overflowY: 'auto' }}>
      {props.templateItems.map((template) => (
        <div role="listitem" key={template.taskId}>
          <IPActionButton
            iconProps={template.taskType === TaskTypes.Template ? taskIcon : eventIcon}
            text={template.name}
            onClick={() => props.onSelect(template)}
            styles={{
              root: {
                width: '100%',
                minHeight: 40,
                justifyContent: 'flex-start',
              },
              label: { textAlign: 'left', whiteSpace: 'normal', lineHeight: 20 },
            }}
          />
        </div>
      ))}
    </div>
  );
});
